import { inject, Injectable } from '@angular/core';
import { ClientDatasetFilterService } from '@ddv/filters';
import { DatasetFetchRuntimeParams, Query } from '@ddv/models';
import { catchError, combineLatest, map, Observable, of } from 'rxjs';

import { CrosstalkDataService } from './crosstalk-data.service';
import { DatasetTrebekQueryRunnerService } from './dataset-trebek-query-runner.service';
import { PublicAPIQueryResponse, PublicApiResponseRow, TrebekError } from '../models/trebek';

export interface Dataset extends PublicAPIQueryResponse {
    trebekError?: TrebekError;
}

@Injectable()
export class DatasetQueryRunnerService {
    private readonly trebekInvoker = inject(DatasetTrebekQueryRunnerService);
    private readonly clientDatasetFilterService = inject(ClientDatasetFilterService);
    private readonly crosstalkDataService = inject(CrosstalkDataService);

    runQueries(
        clientCode: string,
        widgetIds: number[],
        queries: Query[],
        queryEndpoint: string,
        statusEndpoint: string,
        runtimeParams: DatasetFetchRuntimeParams,
    ): Observable<Dataset> {
        const queries$: Observable<Dataset>[] = queries.map((dataQuery) => {
            return this.invokePublicAPI(
                widgetIds,
                dataQuery,
                queryEndpoint,
                statusEndpoint,
                runtimeParams.clientCodeList,
            );
        });

        return combineLatest(queries$)
            .pipe(
                map((responses) => {
                    const queryError = responses.find((response) => response.trebekError)?.trebekError;
                    if (queryError) {
                        return {
                            trebekError: queryError,
                            data: [],
                        };
                    }

                    const data = this.combineDatasets(
                        responses.map((response) => response.data),
                        queries[0].selectedColumns);

                    data.forEach((datum) => this.crosstalkDataService.addCrosstalkLinksToData(clientCode, datum));
                    return { data };
                }),
            );
    }

    private invokePublicAPI(
        widgetIds: number[],
        dataQuery: Query,
        queryEndpoint: string,
        statusEndpoint: string,
        clientCodeList: string[] | undefined,
    ): Observable<PublicAPIQueryResponse & { trebekError?: TrebekError }> {
        return this.trebekInvoker
            .runQuery(
                widgetIds,
                dataQuery,
                queryEndpoint,
                statusEndpoint,
                clientCodeList,
            )
            .pipe(
                catchError((error) => {
                    return of({
                        trebekError: error as TrebekError,
                        data: [],
                    });
                }),
            );
    }

    private combineDatasets(datas: PublicApiResponseRow[][], columns: { columnId: string }[] | undefined): PublicApiResponseRow[] {
        if (datas.length === 1 || !columns?.length) {
            return datas[0];
        }

        return this.clientDatasetFilterService.removeDuplicateRows(
            datas.flatMap((data) => data),
            columns.map((column) => column.columnId));
    }
}
